import { createSignal, For, JSX } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { CategoryData } from "~/server/types/category";
import { useNavigate } from "@solidjs/router";
import { getCategorySlugFromCategoryName } from "~/utils/common";
import { goBack } from "~/shared_states/modal";
import { categoryIconMap } from "~/assets/category_icons";

export type CategoriesModalProps = {
  categories: CategoryData[];
};

type SortOption = "popularity" | "alphabetical";

/**
 * CategoriesModal Component
 * A modal displaying all categories with sorting options (popularity/alphabetically)
 * Based on Figma design: https://www.figma.com/design/uTQhl6K0EfVzl9uxl4Y4o1/Demand-master?node-id=262-10073
 */
export function CategoriesModal(props: CategoriesModalProps): JSX.Element {
  const { components } = useTelescopeContext();
  const [sortBy, setSortBy] = createSignal<SortOption>("popularity");
  const navigate = useNavigate();

  // Sort categories based on selected option
  const sortedCategories = () => {
    if (sortBy() === "alphabetical") {
      return [...props.categories].sort((a, b) =>
        a.metadata.title.localeCompare(b.metadata.title)
      );
    }
    // Default: popularity order (as received from API)
    return props.categories;
  };

  return (
    <div class=" flex flex-col ">
      {/* Modal Header */}
      <div class="sticky top-0">
        <components.ModalHeader
          size="small"
          title="Browse categories"
          onClose={() => {
            goBack();
          }}
        />
      </div>

      {/* Content */}
      <div class="flex w-full flex-1 flex-col items-start overflow-hidden">
        {/* Segmented Control */}
        <div class="box-border flex w-full flex-col gap-[10px] px-5 py-4">
          <components.SegmentedControl
            items={[
              { id: "popularity", label: "By Popularity" },
              { id: "alphabetical", label: "Alphabetically" },
            ]}
            activeItem={sortBy()}
            onItemChange={(itemId) => setSortBy(itemId as SortOption)}
            type="label-only"
            size="small"
            expanded={true}
          />
        </div>

        {/* Categories Grid */}
        <div class="no-scrollbar box-border grid w-full flex-1 grid-cols-3 gap-3 overflow-y-auto px-5 pb-5">
          <For each={sortedCategories()}>
            {(category) => (
              <div
                onClick={() => {
                  goBack(() => {
                    navigate(
                      `/shop-by-category/${getCategorySlugFromCategoryName(category.name)}`
                    );
                  });
                }}
              >
                <CategoryCard category={category} />
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

/**
 * Individual Category Card
 */
function CategoryCard(props: { category: CategoryData }): JSX.Element {
  const [isHovered, setIsHovered] = createSignal(false);

  // Check if icon component exists in the map
  const IconComponent = categoryIconMap[props.category.name];

  return (
    <div
      class="box-border flex h-full w-full cursor-pointer flex-col gap-[10px] rounded-xl border border-solid border-stroke-1 px-0 py-3 transition-colors duration-200"
      classList={{
        "bg-background-normal-secondary": isHovered(),
        "bg-background-normal-primary": !isHovered(),
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div class="box-border flex w-full flex-col items-center gap-[6px] stroke-text-normal-secondary px-0 py-1">
        {/* Icon */}
        {IconComponent ? (
          <IconComponent class="size-6 shrink-0 " />
        ) : (
          <img
            src={props.category.metadata.iconUrl}
            alt={props.category.metadata.title}
            class="size-6 shrink-0"
          />
        )}

        {/* Label */}
        <div class="flex w-full min-w-full flex-col justify-center overflow-hidden text-center text-label-medium leading-4">
          <p
            class="overflow-hidden overflow-ellipsis whitespace-nowrap px-1"
            classList={{
              "text-text-normal-primary": isHovered(),
              "text-text-normal-secondary": !isHovered(),
            }}
          >
            {props.category.metadata.title}
          </p>
        </div>
      </div>
    </div>
  );
}
